import * as THREE from 'three'
import {useThree} from '@react-three/fiber'
import {useGLTF, Outlines} from '@react-three/drei'
import {useCallback, useEffect, useMemo, useRef, useState,} from 'react'
import {SelectionHover} from './SelectionHover.jsx'
import {usePulsatingOutline} from '../hooks/usePulsatingOutline.jsx'
import {useAppStore} from '../stores/store.js'
import {INFO_PANEL_DATA, TABLET_BOX_PROPS} from '../utils/constants.jsx'
import {gsap} from 'gsap'

const SONG_COLORS = [
    new THREE.Color('#a77554'), // Corresponds to Baekhyun gradient hint
    new THREE.Color('#8c5f46'), // Corresponds to D.O. gradient hint
    new THREE.Color('#8c5662'), // Corresponds to EXO gradient hint
    new THREE.Color('#d9c098'), // Corresponds to GW gradient hint
    new THREE.Color('#733439'), // Corresponds to RV gradient hint
];

const DEFAULT_SCREEN_COLOR = new THREE.Color('#222222');

export function TabletModel(props) {
    const {nodes, materials} = useGLTF('./models/tablet.glb')
    const tabletRef = useRef(null)
    const [hovered, setHovered] = useState(false)
    const {camera, size} = useThree()

    const outlineThickness = usePulsatingOutline(0.01, 0.03, 2); // Adjust values as needed

    const selectedSongIndex = useAppStore((state) => state.selectedSong);
    const isTabletOpen = useAppStore((state) => state.isTabletOpen);

    const customTabletMaterials = useMemo(() => {
        console.log('Creating base tablet materials');
        const result = {};
        Object.entries(materials).forEach(([name, originalMaterial]) => {
            // Start screen with default color or maybe first song color?
            const initialColor = name === 'phone_screen'
                ? (DEFAULT_SCREEN_COLOR || SONG_COLORS[0]) // Use first song's color initially
                : originalMaterial.color;

            result[name] = new THREE.MeshBasicMaterial({
                map: originalMaterial.map, // Keep original map if screen has one
                color: initialColor,
            });
            // Make screen emissive if desired?
            // if (name === 'phone_screen') {
            //     result[name].emissive = initialColor;
            //     result[name].emissiveIntensity = 0.5; // Adjust intensity
            // }
        });
        return result;
        // Only depend on the loaded materials, not the song index
    }, [materials]);

    useEffect(() => {
        const screenMaterial = customTabletMaterials.phone_screen;
        if (!isTabletOpen) {
            gsap.to(screenMaterial.color, {
                r: DEFAULT_SCREEN_COLOR.r,
                g: DEFAULT_SCREEN_COLOR.g,
                b: DEFAULT_SCREEN_COLOR.b,
                duration: 0.5, // Adjust duration
                ease: 'power1.inOut'
            });
            return;
        }
        if (screenMaterial) {
            const newColor = SONG_COLORS[selectedSongIndex] || DEFAULT_SCREEN_COLOR;
            console.log(`Updating screen color for song index ${selectedSongIndex} to:`, newColor.getHexString());
            // Animate the color change smoothly (optional)
            gsap.to(screenMaterial.color, {
                r: newColor.r,
                g: newColor.g,
                b: newColor.b,
                duration: 0.5, // Adjust duration
                ease: 'power1.inOut'
            });
            // Or set directly:
            // screenMaterial.color.set(newColor);

            // Also update emissive if you are using it
            // if (screenMaterial.emissive) {
            //    gsap.to(screenMaterial.emissive, { ... })
            // }
        }
        // Depend on the song index and the material instance
    }, [selectedSongIndex, customTabletMaterials.phone_screen, isTabletOpen]);

    const handleClick = useCallback((event) => {
        event.stopPropagation()

        if (!tabletRef.current || !camera || !size.width || !size.height) {
            console.warn('Click skipped: Missing refs, handlers, or camera/size info.')
            return
        }

        const {focusOnObject, selectObject} = useAppStore.getState()

        // Get World Position
        const worldPos = new THREE.Vector3()
        tabletRef.current.getWorldPosition(worldPos)

        const cameraFocusOffset = new THREE.Vector3(0, 3, -5)
        focusOnObject(worldPos, cameraFocusOffset)
        selectObject(INFO_PANEL_DATA.tablet.id)

    }, [camera, size])

    const handlePointerEnter = useCallback((e) => {
        e.stopPropagation()
        setHovered(true)
        document.body.style.cursor = 'pointer'
    }, [])

    const handlePointerOut = useCallback((e) => {
        e.stopPropagation()
        setHovered(false)
        document.body.style.cursor = 'default'
    }, [])

    return (
        <>
            <group scale={props.scale} position-y={props['position-y'] + 0.1} dispose={null}>
                <mesh
                    geometry={nodes.tablet_1.geometry}
                    material={customTabletMaterials.phone}
                >
                    <Outlines thickness={outlineThickness} color='white' screenspace={true}/>
                </mesh>
                <mesh
                    geometry={nodes.tablet_2.geometry}
                    material={customTabletMaterials.phone_screen}
                >
                    <Outlines thickness={outlineThickness} color='white' screenspace={true}/>
                </mesh>
            </group>

            <mesh
                position={TABLET_BOX_PROPS.position}
                scale={TABLET_BOX_PROPS.scale}
                ref={tabletRef}
                onPointerEnter={handlePointerEnter}
                onPointerOut={handlePointerOut}
                onClick={handleClick}
            >
                <boxGeometry/>
                <meshBasicMaterial
                    visible={false}
                    // color='blue'
                    // opacity={0.5}
                    // transparent={true}
                />
            </mesh>
            <SelectionHover
                objectRef={tabletRef}
                hovered={hovered}
                cornerSize={0.4}
                padding={0.02}
            />
        </>
    )
}

useGLTF.preload('./models/tablet.glb')
